import React, { KeyboardEventHandler, useEffect, useState } from 'react';
import classNames from 'classnames';
import { filterUnpredictableUtf8Characters } from 'ff-filter-characters';
import { clamp } from 'lodash';
import { IconButton } from '../../buttons/icon-button/IconButton';
import { Cell } from '../../grid/Cell';
import { Grid } from '../../grid/Grid';
import { Icon } from '../../icons/Icon';
import styles from '../Input.module.scss';

export const NumberInputField = ({
    onChange, commit, className, small,
    min = 1, max = 999,
    ...props
}: Props) => {

    const [localValue, setLocalValue] = useState<string | null>(null);
    const [valid, setValid] = useState<boolean>(true);

    const reset = () => setLocalValue(props.value.toString());
    useEffect(reset, [props.value]);

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
            props.enter && props.enter();
        }
    };

    const onInput = (input: string) => {

        setLocalValue(input);
        onChange?.(input);

        const parsed = parseInt(input, 10);
        if (!isNaN(parsed) && parsed > min && parsed < max) {
            setValid(true);
            commit(parsed);
        } else {
            setValid(false);
        }
    };

    const apply = (value: number) => {
        const clamped = clamp(value, min, max);
        setValid(true);
        setLocalValue(clamped.toString());
        onChange?.(clamped.toString());
        commit(clamped);
    };

    const decrement = () => apply(Number(localValue) - 1);
    const increment = () => apply(Number(localValue) + 1);

    const value = localValue !== null ? localValue : props.value;

    const classes = classNames(styles.default, {
        [styles.invalid]: !valid,
        [styles.small]: small
    });

    return (
        <Grid gap='s' fullWidth>
            <Cell fit>
                <IconButton onClick={decrement} small={small}>
                    <Icon type='general-ui-minus' size='small' />
                </IconButton>
            </Cell>

            <Cell>
                <label className={classes}>
                    <input type='number'
                        {...props}
                        name={props.label}
                        id={props.label}
                        className={classNames(styles.input, className)}
                        onKeyDown={onKeyDown}
                        min={min} max={max}
                        value={value}
                        onChange={e => onInput(filterUnpredictableUtf8Characters(e.currentTarget.value))} />
                </label>
            </Cell>

            <Cell fit>
                <IconButton onClick={increment} small={small}>
                    <Icon type='general-ui-plus' size='small' />
                </IconButton>
            </Cell>
        </Grid>
    );
};

type Props = {
    value: number;
    label: string;
    small?: boolean;
    className?: string;
    min?: number;
    max?: number;
    commit: (value: number) => void;
    onChange?: (value: string) => void;
    enter?: () => void;
};